import type { KeyboardEvent } from 'react';
import { GripVertical, X } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { MeetingTemplateSection } from '@wundo/shared/meetingTemplate.ts';
import { cn } from '../../lib/cn.ts';
import { IconButton } from '../icon-button/icon-button.tsx';
import { Input } from '../input/input.tsx';
import { Textarea } from '../textarea/textarea.tsx';
import type { MeetingTemplateEditorLabels } from './meeting-template-editor-labels.ts';

export interface SortableTemplateTopicProps {
  /** Stable across moves, so a moved topic keeps its fields and its focus. */
  sortableId: string;
  /** 1-based, as the reader counts. */
  position: number;
  topic: MeetingTemplateSection;
  labels: MeetingTemplateEditorLabels;
  /* A template needs at least one topic. */
  removable: boolean;
  onChange: (patch: Partial<MeetingTemplateSection>) => void;
  onRemove: () => void;
  /* One place up or down, from the keyboard. */
  onMove: (direction: -1 | 1) => void;
}

/**
 * One topic of a template, picked up by its grip. The arrows on the grip move
 * it one place, the same way a template row moves in its list.
 */
export function SortableTemplateTopic({
  sortableId,
  position,
  topic,
  labels,
  removable,
  onChange,
  onRemove,
  onMove,
}: SortableTemplateTopicProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: sortableId });

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    /* Or the page scrolls away under the topic that was just moved. */
    event.preventDefault();
    onMove(event.key === 'ArrowUp' ? -1 : 1);
  }

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform) ?? undefined,
        transition,
      }}
      className={cn(
        'flex flex-col gap-2 border-t border-fill px-5.5 py-4 first:border-t-0',
        isDragging && 'relative z-10 bg-surface shadow-raise',
      )}
    >
      <div className='flex items-center gap-2'>
        <IconButton
          size='sm'
          shape='square'
          className='cursor-grab'
          aria-label={labels.moveTopic({ position })}
          {...attributes}
          {...listeners}
          /* After the library's own, so the arrows are ours to answer. */
          onKeyDown={handleKeyDown}
        >
          <GripVertical aria-hidden='true' className='size-4' />
        </IconButton>
        <Input
          aria-label={labels.topicTitleLabel({ position })}
          value={topic.title}
          placeholder={labels.topicTitlePlaceholder}
          onChange={(event) => onChange({ title: event.target.value })}
        />
        <IconButton
          aria-label={labels.removeTopic({ position })}
          variant='danger'
          size='sm'
          disabled={!removable}
          onClick={onRemove}
        >
          <X aria-hidden='true' className='size-4' />
        </IconButton>
      </div>
      <Textarea
        aria-label={labels.topicDescriptionLabel({ position })}
        value={topic.description}
        placeholder={labels.topicDescriptionPlaceholder}
        onChange={(event) => onChange({ description: event.target.value })}
      />
    </div>
  );
}
